import { ApiResult, HttpClient } from "../http.js";

export interface EconomicInstallUrlDto {
  url: string;
}

export interface DineroInstallUrlDto {
  url: string;
}

export interface PushAllCustomersResult {
  created: number;
  linked: number;
  skipped: number;
  errors: Array<{ customerId: string; customerName: string; message: string }>;
}

export interface PushAllProductsResult {
  created: number;
  skipped: number;
  errors: number;
}

/** Stats returned by the on-demand e-conomic PULL sync endpoints (customers/products). */
export interface EconomicSyncStatsResult {
  created: number;
  upserted: number;
  total: number;
}

export interface EconomicMobileBookInvoiceResponse {
  bookedInvoiceNumber: number;
}

export interface BookAndSendEconomicMobileInvoicePayload {
  method: "email" | "mobilePay" | "ean";
}

export interface SendEconomicMobileBookedInvoicePayload {
  method: "email" | "mobilePay" | "ean";
}

export interface RegisterEconomicMobilePaymentPayload {
  customerNumber: number;
  accountNumber: number;
  amount: number;
  currency: string;
  /** YYYY-MM-DD */
  paidOn: string;
  journalNumber?: number;
  description?: string;
}

export interface RegisterEconomicMobilePaymentResponse {
  voucherNumber: number;
}

/** `pending` | `active` | `revoked` | `error` — the Rs.Libs.Integrations `ConnectionStatus` consts. */
export type IntegrationConnectionStatus = "pending" | "active" | "revoked" | "error";

/**
 * One cross-product connection row (Mesterplan ↔ Trackrs etc.) as
 * `GET /api/integrations/connections` lists it for the active org.
 */
export interface IntegrationConnectionDto {
  id: string;
  /** `trackrs`, later `milepro` / `akkordly` / `akui`. */
  remoteProduct: string;
  /** Known once the remote side has activated the handshake; null while pending. */
  remoteCompanyName: string | null;
  status: IntegrationConnectionStatus;
  createdAt: string;
  activatedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
}

export interface InitiateConnectionPayload {
  remoteProduct: string;
}

export interface InitiateConnectionResult {
  connectionId: string;
  /** The remote product's `/integrations/connect?from=mesterplan&connection=<id>` page. */
  redirectUrl: string;
}

export interface AcceptConnectionPayload {
  connectionId: string;
  fromProduct: string;
  /** The Mesterplan company the admin chose to link — authorised per company, not per active org. */
  localCompanyId: string;
}

export interface AcceptConnectionResult {
  /** The remote product's integrations page, `?connected=mesterplan`. */
  redirectUrl: string;
}

// --- "Slå Trackrs til" from inside Mesterplan (case 3, M3–M5) ----------------------------

/**
 * The company's standing with the "may enable Trackrs" staff grant (M1):
 * `none` = never granted, `granted` = live, `cancelled` = switched off by the Owner
 * (a new grant is needed before it can come back).
 */
export type TrackrsModuleState = "none" | "granted" | "cancelled";

/** The company's current (active, else live pending) Trackrs connection, when there is one. */
export interface TrackrsOfferConnectionDto {
  status: IntegrationConnectionStatus;
  remoteCompanyName: string | null;
}

/** What `POST enable` sends to Trackrs's provision receiver — editable on the card. */
export interface TrackrsCompanyPrefillDto {
  name: string;
  cvr: string;
  address: string;
  postalCode: string;
  city: string;
}

/**
 * `GET /api/integrations/trackrs/offer`. `canEnable` is Owner AND a live grant AND Trackrs is
 * not already the billing hub the other way round (case 1/2); `isEnabled` is an active
 * connection billed through Mesterplan.
 */
export interface TrackrsOfferDto {
  canEnable: boolean;
  isOwner: boolean;
  isEnabled: boolean;
  moduleState: TrackrsModuleState;
  connection: TrackrsOfferConnectionDto | null;
  companyPrefill: TrackrsCompanyPrefillDto;
}

/** Body of `POST /api/integrations/trackrs/enable`. */
export interface EnableTrackrsPayload {
  companyName: string;
  cvr: string;
  address: string;
  postalCode: string;
  city: string;
  /** Off by default: the Owner tells the team first. */
  sendWelcomeMails: boolean;
  /**
   * The Owner's "Brug den eksisterende virksomhed" after a 409 `company_exists` where they
   * are admin there: take that Trackrs company over instead of creating one. Omitted on a
   * first enable.
   */
  attachExisting?: boolean;
}

export interface EnableTrackrsResult {
  /** Where to open Trackrs afterwards (the customer's web app). */
  trackrsUrl: string;
}

/** The 409 `company_exists` / `company_ambiguous` payload: Trackrs already has a company on that CVR. */
export interface TrackrsEnableConflictDto {
  code: "company_exists" | "company_ambiguous" | string;
  existingCompanyName: string | null;
  /** True when the enabling Owner already holds an owner/admin membership there. */
  callerIsAdminThere: boolean;
}

/**
 * Error body of a failed `POST enable`, as `ApiError.body`: 409 carries `error` + `conflict`
 * (or `error: "connection_exists"` + `message`), 502 `error: "remote_unavailable"`, 404
 * `error: "not_available"`. A 400 is ProblemDetails with the field map under `ApiError.errors`.
 */
export interface EnableTrackrsErrorBody {
  error?: string;
  message?: string;
  conflict?: TrackrsEnableConflictDto;
}

// --- Trackrs attention nudge ("Vi har brug for din hjælp her", M7a) -----------------------

/** One ranked suggestion for a "sager uden ..." row: a value to pre-fill, plus the Danish label. */
export interface TrackrsAttentionSuggestion {
  code: string;
  label: string;
  value: string | null;
  /** Set only for a customer-picking suggestion; null for "opret ny kunde" and the others. */
  customerId: string | null;
}

/** "open" while unreviewed, "deferred" after "Senere" — never "confirmed"/"rejected" here, those leave the section. */
export type TrackrsSameProjectState = "open" | "deferred";

export interface TrackrsSameProjectItem {
  kind: "same_project";
  /** The match-suggestion id — pass this as `matches/{id}/...`. */
  subjectId: string;
  state: TrackrsSameProjectState;
  mesterplanProjectId: string;
  mesterplanTitle: string;
  mesterplanSite: string | null;
  mesterplanCaseNumber: string | null;
  trackrsRef: string;
  trackrsTitle: string | null;
  trackrsSite: string | null;
  /** Danish text, e.g. `"Samme sagsnummer 20289 i titlen"`. */
  reasons: string[];
}

export interface TrackrsMissingCustomerItem {
  kind: "missing_customer";
  /** The Mesterplan project id — pass this as `dismiss`'s `subjectId`. */
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  site: string | null;
  suggestions: TrackrsAttentionSuggestion[];
}

export interface TrackrsMissingCaseNumberItem {
  kind: "missing_case_number";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  suggestions: TrackrsAttentionSuggestion[];
}

export interface TrackrsMissingStartDateItem {
  kind: "missing_start_date";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  /** YYYY-MM-DD, or null when the Trackrs creation date isn't known. */
  suggestedStartDate: string | null;
}

export interface TrackrsMissingBudgetItem {
  kind: "missing_budget";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  /** Always true — informational, never blocking. */
  optional: true;
}

export interface TrackrsConflictItem {
  kind: "conflict";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  field: "title" | "siteAddress" | "endDate" | "closed";
  keptValue: string | null;
  keptFrom: "mesterplan" | "trackrs";
  lostValue: string | null;
  lostFrom: "mesterplan" | "trackrs";
  at: string;
}

export interface TrackrsRemoteDeletedItem {
  kind: "remote_deleted";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
}

export interface TrackrsAutoLinkedItem {
  kind: "auto_linked";
  subjectId: string;
  projectNumber: string;
  projectTitle: string;
  trackrsRef: string;
}

/** `GET /api/integrations/trackrs/attention` — the full nudge model. */
export interface TrackrsAttentionDto {
  openCount: number;
  /** True when the Trackrs pull failed; sameProject/missingCustomer(c)/missingCaseNumber(a) are then built without Trackrs text. */
  remoteUnavailable: boolean;
  /** Set while "Ikke nu" is active — every section is then empty. */
  snoozedUntil: string | null;
  sameProject: TrackrsSameProjectItem[];
  missingCustomer: TrackrsMissingCustomerItem[];
  missingCaseNumber: TrackrsMissingCaseNumberItem[];
  missingStartDate: TrackrsMissingStartDateItem[];
  missingBudget: TrackrsMissingBudgetItem[];
  conflicts: TrackrsConflictItem[];
  remoteDeleted: TrackrsRemoteDeletedItem[];
  autoLinked: TrackrsAutoLinkedItem[];
}

/** `{ kind, subjectId?, snoozeDays? }` for `POST attention/dismiss`. `subjectId` is required for every kind except `whole_page`. */
export interface DismissTrackrsAttentionPayload {
  kind:
    | "missing_customer"
    | "missing_case_number"
    | "missing_budget"
    | "missing_start_date"
    | "conflict"
    | "remote_deleted"
    | "auto_linked"
    | "whole_page";
  subjectId?: string;
  /** Only meaningful for `whole_page`; defaults to 7 server-side. */
  snoozeDays?: number;
}

/**
 * `/api/integrations/*` — integration connect/disconnect endpoints.
 * These wrap the .NET EconomicAuthController + future integration endpoints.
 */
export class IntegrationsApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * GET /api/integrations/economic/install-url
   * Returns the e-conomic install URL including a signed state parameter.
   * The caller should redirect `window.location.href` to the returned URL.
   */
  getEconomicInstallUrl(): Promise<ApiResult<EconomicInstallUrlDto>> {
    return this.http.get<EconomicInstallUrlDto>("/api/integrations/economic/install-url");
  }

  /**
   * POST /api/integrations/economic/disconnect
   * Disconnects the e-conomic integration for the caller's active org.
   * Deletes the grant token from Key Vault and marks the integration as disconnected.
   */
  disconnectEconomic(): Promise<ApiResult<void>> {
    return this.http.post<void>("/api/integrations/economic/disconnect");
  }

  /**
   * POST /api/integrations/economic/customers/push-all
   * Pushes all unlinked Mesterplan customers for the caller's org to e-conomic.
   * Matches by name; returns stats (created / linked / skipped / errors).
   *
   * Supersedes the legacy frontend-side `useSyncEconomicCustomers` from PR #304.
   */
  pushAllCustomersToEconomic(): Promise<ApiResult<PushAllCustomersResult>> {
    return this.http.post<PushAllCustomersResult>(
      "/api/integrations/economic/customers/push-all"
    );
  }

  /**
   * POST /api/integrations/economic/products/push-all
   * Pushes all org products to e-conomic. Matches by SKU; creates when no match exists.
   * Returns stats: { created, skipped, errors }.
   */
  pushAllProducts(): Promise<ApiResult<PushAllProductsResult>> {
    return this.http.post<PushAllProductsResult>("/api/integrations/economic/products/push-all");
  }

  /**
   * POST /api/integrations/economic/customers/sync
   * On-demand PULL: imports customers modified in e-conomic into Mesterplan (same
   * logic as the nightly PullEconomicCustomers sweep). Returns { created, upserted, total }.
   */
  syncEconomicCustomers(): Promise<ApiResult<EconomicSyncStatsResult>> {
    return this.http.post<EconomicSyncStatsResult>("/api/integrations/economic/customers/sync");
  }

  /**
   * POST /api/integrations/economic/products/sync
   * On-demand PULL: links products from e-conomic to Mesterplan by SKU (same logic
   * as the nightly PullEconomicProducts sweep). Returns { created, upserted, total }.
   */
  syncEconomicProducts(): Promise<ApiResult<EconomicSyncStatsResult>> {
    return this.http.post<EconomicSyncStatsResult>("/api/integrations/economic/products/sync");
  }

  bookEconomicInvoiceDraft(
    draftInvoiceNumber: number,
  ): Promise<ApiResult<EconomicMobileBookInvoiceResponse>> {
    return this.http.post<EconomicMobileBookInvoiceResponse>(
      `/api/integrations/economic/invoices/drafts/${encodeURIComponent(String(draftInvoiceNumber))}/book`,
    );
  }

  bookAndSendEconomicInvoiceDraft(
    draftInvoiceNumber: number,
    payload: BookAndSendEconomicMobileInvoicePayload,
  ): Promise<ApiResult<EconomicMobileBookInvoiceResponse>> {
    return this.http.post<EconomicMobileBookInvoiceResponse>(
      `/api/integrations/economic/invoices/drafts/${encodeURIComponent(String(draftInvoiceNumber))}/book-and-send`,
      { body: payload },
    );
  }

  sendEconomicBookedInvoice(
    bookedInvoiceNumber: number,
    payload: SendEconomicMobileBookedInvoicePayload,
  ): Promise<ApiResult<void>> {
    return this.http.post<void>(
      `/api/integrations/economic/invoices/booked/${encodeURIComponent(String(bookedInvoiceNumber))}/send`,
      { body: payload },
    );
  }

  registerEconomicInvoicePayment(
    bookedInvoiceNumber: number,
    payload: RegisterEconomicMobilePaymentPayload,
  ): Promise<ApiResult<RegisterEconomicMobilePaymentResponse>> {
    return this.http.post<RegisterEconomicMobilePaymentResponse>(
      `/api/integrations/economic/invoices/booked/${encodeURIComponent(String(bookedInvoiceNumber))}/payments`,
      { body: payload },
    );
  }

  /**
   * GET /api/integrations/dinero/install-url
   * Returns the Visma Connect authorize URL including a signed state parameter.
   * The caller should redirect `window.location.href` to the returned URL.
   *
   * The OAuth code exchange happens entirely in the backend callback — the browser
   * never handles a Dinero code, token, or client secret (issue #2287).
   */
  getDineroInstallUrl(): Promise<ApiResult<DineroInstallUrlDto>> {
    return this.http.get<DineroInstallUrlDto>("/api/integrations/dinero/install-url");
  }

  /**
   * POST /api/integrations/dinero/disconnect
   * Disconnects Dinero for the caller's active org. Deletes the refresh token from
   * Key Vault and marks the integration as disconnected.
   */
  disconnectDinero(): Promise<ApiResult<void>> {
    return this.http.post<void>("/api/integrations/dinero/disconnect");
  }

  /**
   * `{METHOD} /api/integrations/dinero/passthrough/{version}/{path}`
   *
   * Forwards one call to api.dinero.dk. The backend attaches the org's Dinero access
   * token, which it derives from the refresh token in Key Vault — the browser holds no
   * Dinero credential (issue #2287).
   */
  dineroPassthrough<T>(request: DineroPassthroughPayload): Promise<ApiResult<T>> {
    // Encode per segment: guids and product numbers are path data, but the separators
    // must survive. The server re-validates the decoded path against a whitelist.
    const encodedPath = request.path
      .split("/")
      .filter((segment) => segment.length > 0)
      .map(encodeURIComponent)
      .join("/");

    return this.http.request<T>(
      request.method,
      `/api/integrations/dinero/passthrough/${request.version}/${encodedPath}`,
      request.body === undefined ? {} : { body: request.body },
    );
  }

  /**
   * GET /api/integrations/connections — every cross-product connection row of the
   * active org, newest first, revoked ones included. Requires
   * `integrations.connections.manage`; answers 404 while `RsIntegrations:Enabled`
   * is off on the backend, which callers treat as "feature not available".
   */
  listConnections(): Promise<ApiResult<IntegrationConnectionDto[]>> {
    return this.http.get<IntegrationConnectionDto[]>("/api/integrations/connections");
  }

  /**
   * POST /api/integrations/connections — starts a handshake as the initiator. The
   * browser must then navigate to `redirectUrl` (the remote product's connect page).
   * 409 when a pending/active connection to that product already exists.
   */
  initiateConnection(
    payload: InitiateConnectionPayload,
  ): Promise<ApiResult<InitiateConnectionResult>> {
    return this.http.post<InitiateConnectionResult>("/api/integrations/connections", {
      body: payload,
    });
  }

  /**
   * POST /api/integrations/connections/accept — completes a handshake the remote
   * product initiated. 403 when the caller is not admin of `localCompanyId` or is
   * not the RsConnect user who started it; 409 when the handshake is gone, expired
   * or a connection already exists; 502 when the remote product could not be reached.
   */
  acceptConnection(payload: AcceptConnectionPayload): Promise<ApiResult<AcceptConnectionResult>> {
    return this.http.post<AcceptConnectionResult>("/api/integrations/connections/accept", {
      body: payload,
    });
  }

  /** DELETE /api/integrations/connections/{id} — revokes locally and best-effort remotely. */
  revokeConnection(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/integrations/connections/${encodeURIComponent(id)}`);
  }

  // --- Trackrs attention nudge ("Vi har brug for din hjælp her", M7a) ---------------------

  /** GET /api/integrations/trackrs/attention — the full nudge model for the active org. */
  getTrackrsAttention(): Promise<ApiResult<TrackrsAttentionDto>> {
    return this.http.get<TrackrsAttentionDto>("/api/integrations/trackrs/attention");
  }

  /** POST /api/integrations/trackrs/matches/{id}/confirm — "Samme sag": links the pair. */
  confirmTrackrsMatch(id: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/integrations/trackrs/matches/${encodeURIComponent(id)}/confirm`);
  }

  /** POST /api/integrations/trackrs/matches/{id}/reject — "To forskellige sager". */
  rejectTrackrsMatch(id: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/integrations/trackrs/matches/${encodeURIComponent(id)}/reject`);
  }

  /** POST /api/integrations/trackrs/matches/{id}/defer — "Senere": keeps showing the suggestion. */
  deferTrackrsMatch(id: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/integrations/trackrs/matches/${encodeURIComponent(id)}/defer`);
  }

  /** POST /api/integrations/trackrs/matches/create-remaining — "Opret resten som nye sager". */
  createRemainingTrackrsMatches(): Promise<ApiResult<number>> {
    return this.http.post<number>("/api/integrations/trackrs/matches/create-remaining");
  }

  /** POST /api/integrations/trackrs/links/{projectId}/unlink — undoes a link (e.g. a wrong auto-link). */
  unlinkTrackrsProject(projectId: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/integrations/trackrs/links/${encodeURIComponent(projectId)}/unlink`);
  }

  /**
   * POST /api/integrations/trackrs/conflicts/{projectId}/undo — "Fortryd" on a merge conflict.
   * Restores the lost value as a real local edit, which propagates to Trackrs.
   */
  undoTrackrsConflict(projectId: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/integrations/trackrs/conflicts/${encodeURIComponent(projectId)}/undo`);
  }

  /** POST /api/integrations/trackrs/attention/dismiss — "Ignorér" a row, or "Ikke nu" the whole page. */
  dismissTrackrsAttention(payload: DismissTrackrsAttentionPayload): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>("/api/integrations/trackrs/attention/dismiss", { body: payload });
  }

  // --- "Slå Trackrs til" from inside Mesterplan (case 3) ------------------------------------

  /**
   * GET /api/integrations/trackrs/offer — whether and how the active org can switch Trackrs
   * on from here. Requires `integrations.connections.manage`; 404 while `RsIntegrations:Enabled`
   * is off on the backend, which callers treat as "feature not available".
   */
  getTrackrsOffer(): Promise<ApiResult<TrackrsOfferDto>> {
    return this.http.get<TrackrsOfferDto>("/api/integrations/trackrs/offer");
  }

  /**
   * POST /api/integrations/trackrs/enable — Mesterplan creates (or, with `attachExisting`,
   * takes over) the Trackrs company and activates the connection; the browser never leaves.
   * 409 `company_exists` / `company_ambiguous` / `connection_exists`, 400 with field errors,
   * 502 `remote_unavailable` (nothing changed — retry), 403 not the Owner, 404 `not_available`.
   */
  enableTrackrs(payload: EnableTrackrsPayload): Promise<ApiResult<EnableTrackrsResult>> {
    return this.http.post<EnableTrackrsResult>("/api/integrations/trackrs/enable", { body: payload });
  }

  /**
   * POST /api/integrations/trackrs/cancel — Owner-only. Cancels the staff grant and tells
   * Trackrs, which locks the company (data kept). The connection row stays. 204 on success.
   */
  cancelTrackrs(): Promise<ApiResult<void>> {
    return this.http.post<void>("/api/integrations/trackrs/cancel");
  }
}

export interface DineroPassthroughPayload {
  method: "GET" | "POST" | "PUT" | "DELETE";
  version: "v1" | "v2";
  /** Path below the version segment, no leading slash. */
  path: string;
  body?: Record<string, unknown>;
}
